/**
 * downloadOnePager.ts — turn an ExportBundle into a downloaded PDF in the browser (T-258).
 *
 * The thin, browser-only tail of the export: render the document to a Blob via @react-pdf/renderer
 * and click a temporary object-URL anchor. Kept tiny and side-effecting; all the logic that decides
 * WHAT the document says lives in the pure, tested layers above it. Verified by a live download,
 * not a jsdom unit test (Blob/URL/anchor-click are browser primitives).
 */
import { pdf } from '@react-pdf/renderer'
import { OnePagerDocument } from './OnePagerDocument'
import { OnePagerDocumentV2 } from './OnePagerDocumentV2'
import type { ExportBundle, ExportBundleV2 } from '../../lib/pdf/onePagerExport'

async function downloadBlob(blob: Blob, filename: string): Promise<void> {
  const url = URL.createObjectURL(blob)
  try {
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
  } finally {
    URL.revokeObjectURL(url)
  }
}

export async function downloadOnePager(bundle: ExportBundle): Promise<void> {
  const doc = OnePagerDocument({ model: bundle.model })
  const blob = await pdf(doc).toBlob()
  await downloadBlob(blob, bundle.filename)
}

export async function downloadOnePagerV2(bundle: ExportBundleV2): Promise<void> {
  const doc = OnePagerDocumentV2({ model: bundle.model })
  const blob = await pdf(doc).toBlob()
  await downloadBlob(blob, bundle.filename)
}
